import React, { useState, useEffect, useMemo } from 'react'
import { Box, Button, VStack, HStack, Text, Select, Icon, Input, Badge, FormControl, Slider } from 'native-base'
import { MaterialIcons } from '@expo/vector-icons'
import { Controller, useFormContext } from 'react-hook-form'
import moment from 'moment'

enum CronMode {
  Recurring = 'recurring',
  FixedHours = 'fixedHours',
  Manual = 'manual',
}

export default function InputCronScheduler(props: {
  label: string
  name: string
  isRequired?: boolean
  nextRun: string | null
}) {
  const form = useFormContext()
  const { invalid } = form.getFieldState(props.name)
  return (
    <FormControl isInvalid={invalid} isRequired={props.isRequired} flex={1} testID={`field_phone_input`}>
      <FormControl.Label>{props.label}</FormControl.Label>
      <Controller
        name={props.name}
        control={form.control}
        render={({ field }) => {
          return (
            <CronScheduler
              value={field.value}
              onChange={(cronState) => {
                field.onChange(cronState.expression)
              }}
            />
          )
        }}></Controller>
      {props.nextRun && (
        <Box paddingTop={2}>
          <Text fontWeight={'bold'}>Next scheduled run: {moment(props.nextRun).format('LLL')}</Text>
        </Box>
      )}
    </FormControl>
  )
}

// function validateCronState(state: CronState): null | string {
//   switch (state.mode) {
//     case CronMode.Manual:
//       return null
//     case CronMode.Recurring:
//       if (state.selectedDays?.length == 0) {
//         return 'Please select at least one day from week when this will run'
//       }
//       return state.hourInterval && state.hourInterval > 0 && state.hourInterval < 24
//         ? null
//         : 'Invalid hour interval. Must be between 1 and 23 hours'
//     case CronMode.Recurring:
//       if (state.selectedDays?.length == 0) {
//         return 'Please select at least one day from week when this will run'
//       }
//       return state.selectedHours && state.selectedHours.length > 0 ? null : 'Please select at least one time'
//   }
// }

function parseCronExpression(cron: string | null) {
  if (!cron) {
    return {
      mode: CronMode.Manual,
      hourInterval: 6,
      selectedDays: [0, 1, 2, 3, 4, 5, 6],
      selectedHours: [10],
    }
  }

  const parts = cron.split(' ')
  const minute = parts[0]
  const hour = parts[1]
  const dayOfWeek = parts[4]

  if (hour.includes('/')) {
    // Recurring mode
    const hourInterval = parseInt(hour.split('/')[1], 10)
    const selectedDays = dayOfWeek === '*' ? [0, 1, 2, 3, 4, 5, 6] : dayOfWeek.split(',').map(Number)
    return {
      mode: CronMode.Recurring,
      hourInterval,
      selectedDays,
      selectedHours: [10],
    }
  } else {
    // Fixed hours mode
    const selectedHours = hour.split(',').map(Number)
    const selectedDays = dayOfWeek === '*' ? [0, 1, 2, 3, 4, 5, 6] : dayOfWeek.split(',').map(Number)
    return {
      mode: CronMode.FixedHours,
      hourInterval: 6,
      selectedDays,
      selectedHours,
    }
  }
}

interface CronState {
  mode: CronMode
  hourInterval: null | number
  selectedDays: null | number[]
  selectedHours: null | number[]
  expression: string | null
}

export function CronScheduler(props: { value: string | null; onChange: (state: CronState) => void }) {
  console.log('CronScheduler', props.value)

  const state = useMemo(() => parseCronExpression(props.value), [props.value])

  const [mode, setMode] = useState(state.mode)
  const [hourInterval, setHourInterval] = useState(state.hourInterval)
  const [selectedDays, setSelectedDays] = useState(state.selectedDays)
  const [selectedHours, setSelectedHours] = useState(state.selectedHours)
  const [cronExpression, setCronExpression] = useState(props.value)

  const daysOfWeek = [
    { value: 0, label: 'Sun' },
    { value: 1, label: 'Mon' },
    { value: 2, label: 'Tue' },
    { value: 3, label: 'Wed' },
    { value: 4, label: 'Thu' },
    { value: 5, label: 'Fri' },
    { value: 6, label: 'Sat' },
  ]

  const formatHour = (hour: number) => {
    const h = hour % 12 || 12
    const ampm = hour < 12 ? 'AM' : 'PM'
    return `${h}:00 ${ampm}`
  }

  const toggleDay = (day: number) => {
    if (selectedDays.includes(day)) {
      if (selectedDays.length > 1) {
        setSelectedDays(selectedDays.filter((d) => d !== day))
      }
    } else {
      setSelectedDays([...selectedDays, day].sort((a, b) => a - b))
    }
  }

  const addHour = (hour: number) => {
    if (!selectedHours.includes(hour)) {
      const newHours = [...selectedHours, hour].sort((a, b) => a - b)
      setSelectedHours(newHours)
    }
  }

  const removeHour = (hourToRemove: number) => {
    setSelectedHours(selectedHours.filter((hour) => hour !== hourToRemove))
  }

  const handleIntervalChange = (value: number) => {
    const interval = Math.max(1, Math.min(23, value))
    setHourInterval(interval)
  }

  useEffect(() => {
    let expression = null
    const dayString = selectedDays.length === 7 ? '*' : selectedDays.join(',')

    if (mode === CronMode.Recurring) {
      expression = `0 */${hourInterval} * * ${dayString}`
    } else if (mode === CronMode.FixedHours && selectedHours.length > 0) {
      const hoursString = selectedHours.join(',')
      expression = `0 ${hoursString} * * ${dayString}`
    }
    setCronExpression(expression)
    props.onChange({ mode, hourInterval, selectedHours, selectedDays, expression })
  }, [mode, hourInterval, selectedDays, selectedHours])

  return (
    <Box>
      <VStack space={4}>
        <HStack space={2} mb={4}>
          <Button
            flex={1}
            size="sm"
            colorScheme={mode === CronMode.Manual ? 'secondary' : ''}
            variant={mode == CronMode.Manual ? 'solid' : 'outline'}
            onPress={() => setMode(CronMode.Manual)}>
            Sync on Demand
          </Button>

          <Button
            flex={1}
            size="sm"
            colorScheme={mode === CronMode.Recurring ? 'secondary' : ''}
            variant={mode == CronMode.Recurring ? 'solid' : 'outline'}
            onPress={() => setMode(CronMode.Recurring)}>
            Recurring Interval
          </Button>
          <Button
            flex={1}
            size="sm"
            colorScheme={mode === CronMode.FixedHours ? 'secondary' : ''}
            variant={mode == CronMode.FixedHours ? 'solid' : 'outline'}
            onPress={() => setMode(CronMode.FixedHours)}>
            Specific Hours
          </Button>
        </HStack>

        {mode == CronMode.Manual && (
          <VStack space="2">
            <Text>
              To initiate menu synchronization please go to Menu Management -&gt; POS Dashboard and press on the Sync
              Now button located at the top right corner.
            </Text>
          </VStack>
        )}

        {(mode == CronMode.Recurring || mode == CronMode.FixedHours) && (
          <VStack space={2}>
            <HStack alignItems="center">
              <Icon as={MaterialIcons} name="calendar-today" size="sm" mr={2} color="gray.500" />
              <Text>Run on these days</Text>
            </HStack>
            <HStack space={2} flexWrap="wrap">
              {daysOfWeek.map((day) => (
                <Button
                  key={day.value}
                  onPress={() => toggleDay(day.value)}
                  colorScheme={selectedDays.includes(day.value) ? 'secondary' : ''}
                  size="sm"
                  variant={selectedDays.includes(day.value) ? 'solid' : 'outline'}>
                  {day.label}
                </Button>
              ))}
            </HStack>
          </VStack>
        )}

        {mode === CronMode.Recurring && (
          <VStack space={2}>
            <HStack alignItems="center">
              <Icon as={MaterialIcons} name="refresh" size="sm" mr={2} color="gray.500" />
              <Text>Repeat every</Text>
            </HStack>
            <HStack alignItems="center">
              {false && (
                <Input
                  type="text"
                  min={1}
                  max={12}
                  value={hourInterval.toString()}
                  onChangeText={(value) => handleIntervalChange(parseInt(value))}
                  w={16}
                  mr={2}
                />
              )}
              <Box alignItems="center" w="100%">
                <Slider
                  w="3/4"
                  maxW="300"
                  defaultValue={hourInterval}
                  minValue={1}
                  maxValue={23}
                  accessibilityLabel="Repeat interval"
                  onChange={(n) => handleIntervalChange(n)}
                  step={1}>
                  <Slider.Track>
                    <Slider.FilledTrack />
                  </Slider.Track>
                  <Slider.Thumb />
                </Slider>
                <Text>
                  {hourInterval} hour{hourInterval > 1 ? 's' : ''}
                </Text>
              </Box>
            </HStack>
          </VStack>
        )}

        {mode === CronMode.FixedHours && (
          <VStack space={2}>
            <HStack alignItems="center">
              <Icon as={MaterialIcons} name="access-time" size="sm" mr={2} color="gray.500" />
              <Text>Run at these hours</Text>
            </HStack>

            <VStack space={2} mb={4}>
              {selectedHours.map((hour) => (
                <HStack key={hour} alignItems="center" bg="gray.100" p={2} rounded="md">
                  <Text flex={1}>{formatHour(hour)}</Text>
                  {selectedHours.length > 1 && (
                    <Button onPress={() => removeHour(hour)} colorScheme="red" variant="ghost">
                      <Icon as={MaterialIcons} name="close" size="sm" />
                    </Button>
                  )}
                </HStack>
              ))}
            </VStack>

            <VStack space={2}>
              <Text>Add an hour:</Text>
              <HStack space={2}>
                <Select flex={1} onValueChange={(value) => addHour(parseInt(value))} placeholder="Select hour">
                  {Array.from({ length: 24 }, (_, i) => i).map((hour) => (
                    <Select.Item
                      key={hour}
                      label={formatHour(hour)}
                      value={hour.toString()}
                      isDisabled={selectedHours.includes(hour)}
                    />
                  ))}
                </Select>
                <Button
                  onPress={() => {
                    const availableHours = Array.from({ length: 24 }, (_, i) => i).filter(
                      (h) => !selectedHours.includes(h)
                    )
                    if (availableHours.length > 0) {
                      addHour(availableHours[0])
                    }
                  }}
                  colorScheme="secondary"
                  variant={'outline'}
                  isDisabled={selectedHours.length === 24}>
                  <Icon as={MaterialIcons} name="add" size="sm" />
                </Button>
              </HStack>
            </VStack>
          </VStack>
        )}

        {false && (
          <VStack mt={8} p={4} bg="gray.50" rounded="lg" borderWidth={1} borderColor="gray.200">
            <Text fontWeight="medium" mb={2}>
              Generated Cron Expression:
            </Text>
            <Badge colorScheme="gray" p={2} rounded="md" fontFamily="monospace">
              {cronExpression}
            </Badge>
            <Text mt={2} fontSize="sm" color="gray.600">
              {mode === CronMode.Recurring && (
                <>
                  This will run every {hourInterval} hour{hourInterval > 1 ? 's' : ''} at minute 0
                  {selectedDays.length === 7
                    ? ' every day'
                    : ` on ${selectedDays.map((d) => daysOfWeek.find((day) => day.value === d).label).join(', ')}`}
                </>
              )}
              {mode === CronMode.FixedHours && (
                <>
                  This will run at {selectedHours.map((h) => formatHour(h)).join(', ')}
                  {selectedDays.length === 7
                    ? ' every day'
                    : ` on ${selectedDays.map((d) => daysOfWeek.find((day) => day.value === d).label).join(', ')}`}
                </>
              )}
            </Text>
          </VStack>
        )}
      </VStack>
    </Box>
  )
}
